import SnappyJS from 'snappyjs'
import axios from 'axios'
import Headset from '../enum/headset.ts'
import ModeType from '../enum/ModeType.ts'
import CountryType from '@/enum/CountryType'
import { getMarkLog } from '@/api/http'
import i18n from '@/i18n'

/**
 * @description 解压
 * @param data 解压内容，经过SnappyJS压缩且被btoa转换过的数据
 */
function uncompressed(data: string) {
  const values = atob(data).split(',')
  const len = values.length
  const bytes = new Uint8Array(len)
  for (let i = 0; i < len; i++) {
    bytes[i] = parseInt(values[i], 10)
  }
  const decoder = new TextDecoder()
  const arrayBuffer = SnappyJS.uncompress(bytes)
  const jsonString = decoder.decode(arrayBuffer)
  return jsonString
}

/**
 * @description 获取阿里云数据
 * @param url 数据地址
 */
function getEditorData(url: string) {
  return new Promise((resolve) => {
    axios({
      method: 'get',
      url: url
    }).then(function (res) {
      resolve(uncompressed(res.data))
    })
  })
}

/**
 * @description 判断耳机类型
 * @param name 耳机名称
 */
function getHeadsetType(name: string) {
  switch (name) {
    case Headset.NANOPLUS:
    case 'A05':
    case 'nanoPlus':
    case 'Nano+':
      return Headset.NANOPLUS
    case Headset.RECDOT:
    case 'recdot':
    case 'RecDot':
      return Headset.RECDOT
    case Headset.RECDOT2:
    case 'recdot2':
    case 'RecDot2':
      return Headset.RECDOT2
    case Headset.NOTEKIT:
    case 'notekit':
    case 'Notekit':
    case 'NoteKit':
      return Headset.NOTEKIT
    case Headset.NANOAIR:
    case 'A04':
    case 'nanoAir':
    case 'Air':
      return Headset.NANOAIR
    case Headset.OPENNOTE:
    case 'opennote':
    case 'OpenNote':
      return Headset.OPENNOTE
    default:
      return ''
  }
}

/**
 * @description 获取服务与支持标题
 * @param type 耳机类型（名称）
 */
function getSupportTitle(type: string) {
  switch (type) {
    case Headset.NANOPLUS:
    case 'A05':
    case 'nanoPlus':
    case 'Nano+':
      return 'viaim Nano+'
    case Headset.NANOAIR:
    case 'A04':
    case 'nanoAir':
    case 'Air':
      return 'viaim Air'
    case Headset.RECDOT:
    case 'recdot':
    case 'RecDot':
      return 'viaim RecDot'
    case Headset.RECDOT2:
    case 'recdot2':
    case 'RecDot2':
      return 'viaim RecDot2'
    case Headset.NOTEKIT:
    case 'notekit':
    case 'Notekit':
    case 'NoteKit':
      return 'viaim NoteKit'
    case Headset.OPENNOTE:
    case 'opennote':
    case 'OpenNote':
      return 'viaim OpenNote'
    default:
      return 'viaim Air'
  }
}

/**
 * @description 获取服务与支持banner图片
 * @param type 耳机类型（名称）
 */
function getSupportBannerImg(type: string, isMobile = false) {
  switch (type) {
    case Headset.NANOPLUS:
    case 'A05':
    case 'nanoPlus':
    case 'Nano+':
      return isMobile
        ? 'https://viaim-h5.oss-ap-southeast-1.aliyuncs.com/iflybuds-website-world/images/support/diandu-shadow.png'
        : 'https://viaim-h5.oss-ap-southeast-1.aliyuncs.com/iflybuds-website-world/images/support/diandu.png'
    case Headset.NANOAIR:
    case 'A04':
    case 'nanoAir':
    case 'Air':
      return 'https://viaim-h5.oss-ap-southeast-1.aliyuncs.com/iflybuds-website-world/images/support/Air-card.png'
    case Headset.RECDOT:
    case 'recdot':
    case 'RecDot':
      return 'https://viaim-h5.oss-ap-southeast-1.aliyuncs.com/iflybuds-website-world/images/support/recdot-card.png'
    case Headset.RECDOT2:
    case 'recdot2':
    case 'RecDot2':
      return 'https://viaim-h5.oss-ap-southeast-1.aliyuncs.com/iflybuds-website-world/images/support/recdot2-card.png'
    case Headset.NOTEKIT:
    case 'notekit':
    case 'Notekit':
    case 'NoteKit':
      return 'https://viaim-h5.oss-ap-southeast-1.aliyuncs.com/iflybuds-website-world/images/support/Notekit.png'
    case Headset.OPENNOTE:
    case 'opennote':
    case 'OpenNote':
      return 'https://viaim-h5.oss-ap-southeast-1.aliyuncs.com/iflybuds-website-world/images/support/opennote-card.png'
    default:
      return ''
  }
}

/**
 * @description 获取服务与支持列表页面路径
 * @param type 耳机类型（名称）
 */
function getSupportPath(type: string) {
  switch (type) {
    case Headset.NANOPLUS:
    case 'A05':
    case 'nanoPlus':
    case 'Nano+':
      return 'Nano+'
    case Headset.NANOAIR:
    case 'A04':
    case 'nanoAir':
    case 'Air':
      return 'Air'
    case Headset.NOTEKIT:
    case 'notekit':
    case 'Notekit':
    case 'NoteKit':
      return 'NoteKit'
    case Headset.RECDOT:
    case 'recdot':
    case 'RecDot':
      return 'RecDot'
    case Headset.RECDOT2:
    case 'recdot2':
    case 'RecDot2':
      return 'RecDot2'
    case Headset.OPENNOTE:
    case 'opennote':
    case 'OpenNote':
      return 'OpenNote'
    default:
      return ''
  }
}

/**
 * 发现页埋点配置
 * @param id 文章或视频id
 */
async function getMarkLogs(id: string) {
  if (!id) return
  try {
    const param = {
      market_id: id
    }
    await getMarkLog(param)
  } catch (error) {
    console.log('getMarkLog_error: ', error)
  }
}

/**
 * 获取当前语言的标题和描述
 * @param key 国际化对应的key值
 */
function getLocalizedMeta(key: string) {
  return {
    title: () => i18n.global.t(`Routes.${key}.title`),
    description: () => i18n.global.t(`Routes.${key}.description`)
  }
}

/**
 * 获取服务支持页面的国际化标题和描述
 * @param path 路由路径
 */
function getSupportLocalizedMeta(path: string) {
  // 使用正则去除路由中的语言前缀
  const cleanPath = path.replace(/^\/[a-z]{2}\//, '/')
  console.log('cleanPath: ', cleanPath)
  let key = ''
  switch (cleanPath) {
    case '/support/product/notekit':
      key = 'supportNoteKit'
      break
    case '/support/product/nanoAir':
      key = 'supportAir'
      break
    case '/support/product/nanoPlus':
      key = 'supportNanoPlus'
      break
    case '/support/product/recdot':
      key = 'supportRecDot'
      break
    case '/support/product/recdot2':
      key = 'supportRecDot2'
      break
    default:
      key = 'support'
  }
  return getLocalizedMeta(key)
}

/**
 * 链接跳转
 * @param code 语言代码
 */
function jumpLink(code: string | number) {
  let targetUrl = ''
  switch (code) {
    // 原Global站点
    case -1:
      targetUrl = import.meta.env.VITE_SITE_URL_EN
      break
    // 日本站点
    case 81:
      targetUrl = import.meta.env.VITE_SITE_URL_JP
      break
    // 美国站点
    case 1:
      targetUrl = import.meta.env.VITE_SITE_URL_EN
      break
    // 中东站点
    case 9999:
      targetUrl = import.meta.env.VITE_SITE_URL_AR
      break
  }
  if (targetUrl) {
    if ([1, 9999].includes(Number(code))) {
      // 新标签页打开
      window.open(targetUrl, '_blank')
      return
    } else {
      // 当前页打开
      window.location.href = targetUrl
      return
    }
  }
}

/**
 * 验证是否为有效的视频链接
 * @param url 视频地址
 */
function isValidVideoUrl(url: string) {
  // 检查是否为空
  if (!url || url.trim() === '') {
    return false
  }
  // 检查是否为有效的URL格式
  try {
    new URL(url)
  } catch (e) {
    return false
  }
  // 检查是否包含常见的视频文件扩展名
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.avi', '.mov', '.wmv', '.flv', '.mkv']
  const lowerUrl = url.toLowerCase()
  return videoExtensions.some(ext => lowerUrl.includes(ext))
}

// 验证视频地址是否以固定域名开始
function isUrlFixDomain(url: string): boolean {
  const h5ContentPrefixes = [
    'https://f.viaim.ai/',
    'https://viaim.oss-ap-southeast-1.aliyuncs.com/',
  ]
  return h5ContentPrefixes.some(prefix => url.startsWith(prefix))
}

/**
 * 检查是否为指定构建模式
 * @param mode 构建类型
 */
function isMode(mode: string): boolean {
  if (!mode) return false
  let curMode = ''
  const currentMode = import.meta.env.MODE.replace(/^test-/, '')

  switch (currentMode) {
    case ModeType.DEV:
      curMode = 'development'
      break
    case ModeType.PROD:
      curMode = 'production'
      break
    case ModeType.EN:
      curMode = 'en'
      break
    case ModeType.JP:
      curMode = 'jp'
      break
    case ModeType.KR:
      curMode = 'kr'
      break
    case ModeType.AMZON:
      curMode = 'amazon'
      break
    default:
      curMode = ''
      break
  }
  return curMode === mode
}

/** 获取当前浏览器语言 */
function getBrowserLanguage(): string {
  const browserLanguage = navigator.language.toLowerCase()
  console.log('浏览器语言: ', browserLanguage)
  return browserLanguage
}

/** 是否为韩语站路由 */
function isKoRoute(path: string): boolean {
  const pathSegments = path.split('/').filter(Boolean)
  const isKoBase = import.meta.env.VITE_ROUTER_PATH === '/kr/' || import.meta.env.VITE_BASE_URL === '/kr/'

  return pathSegments[0] === 'kr' || isKoBase
}

/** 获取服务支持区域码 */
function getSupportAreaCode(abb: string, path = window.location.pathname): CountryType {
  if (abb === 'kr' || isKoRoute(path)) return CountryType.KO
  return abb === 'jp' || abb === 'ja' ? CountryType.JA : CountryType.US
}

export {
  uncompressed,
  getEditorData,
  getHeadsetType,
  getSupportTitle,
  getSupportBannerImg,
  getSupportPath,
  getMarkLogs,
  getLocalizedMeta,
  getSupportLocalizedMeta,
  jumpLink,
  isValidVideoUrl,
  isUrlFixDomain,
  isMode,
  getBrowserLanguage,
  isKoRoute,
  getSupportAreaCode,
}
